import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Icon from "components/ShellV2/Icon";

const PT_MONTHS_SHORT = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];
const CAL_MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const CAL_DOW = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const PRESETS = [
  { key: "24h", label: "Últimas 24 horas", days: 1 },
  { key: "7d", label: "Últimos 7 dias", days: 7 },
  { key: "14d", label: "Últimos 14 dias", days: 14 },
  { key: "30d", label: "Últimos 30 dias", days: 30 },
  { key: "90d", label: "Últimos 90 dias", days: 90 },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function isoDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseISO(s) {
  if (!s) return null;
  const parts = s.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function fmtShortYear(d) {
  return `${d.getDate()} ${PT_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function shifted(daysBack) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysBack + 1);
  return d;
}

function today() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function t0(d) {
  return d ? d.getTime() : 0;
}

function detectActivePreset(start, end) {
  const startD = parseISO(start);
  const endD = parseISO(end);
  if (!startD || !endD) return "custom";
  const todayD = today();
  if (t0(endD) !== t0(todayD)) return "custom";
  const found = PRESETS.find((p) => t0(startD) === t0(shifted(p.days)));
  return found ? found.key : "custom";
}

function Calendar({ from, to, onPick }) {
  const initial = from ? parseISO(from) : today();
  const [view, setView] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), 1));
  const y = view.getFullYear();
  const m = view.getMonth();
  const startDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const ndays = new Date(y, m + 1, 0).getDate();
  const fromD = parseISO(from);
  const toD = parseISO(to);
  const todayD = today();

  const cells = useMemo(() => {
    const list = [];
    for (let i = 0; i < startDow; i += 1) list.push(null);
    for (let d = 1; d <= ndays; d += 1) list.push(new Date(y, m, d));
    return list;
  }, [y, m, startDow, ndays]);

  const goPrev = () => setView(new Date(y, m - 1, 1));
  const goNext = () => setView(new Date(y, m + 1, 1));
  const disableNext =
    y > todayD.getFullYear() || (y === todayD.getFullYear() && m >= todayD.getMonth());

  // Year dropdown so users can jump to past years directly (e.g. 2024) instead
  // of clicking the prev-month arrow many times.
  const yearOptions = [];
  for (let yr = todayD.getFullYear(); yr >= 2018; yr -= 1) yearOptions.push(yr);
  const selCls =
    "bg-transparent border-0 p-0 text-ink font-bold text-[13.5px] cursor-pointer outline-none";

  return (
    <div className="rec-cal">
      <div className="rec-cal-head">
        <button type="button" className="rec-cal-icon-btn" onClick={goPrev} title="Mês anterior">
          <Icon name="chevron_left" />
        </button>
        <div className="flex items-center gap-[6px]">
          <select
            className={selCls}
            value={m}
            onChange={(e) => setView(new Date(y, Number(e.target.value), 1))}
            aria-label="Mês"
          >
            {CAL_MONTHS.map((name, i) => (
              <option key={name} value={i}>
                {name}
              </option>
            ))}
          </select>
          <select
            className={selCls}
            value={y}
            onChange={(e) => setView(new Date(Number(e.target.value), m, 1))}
            aria-label="Ano"
          >
            {yearOptions.map((yr) => (
              <option key={yr} value={yr}>
                {yr}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="rec-cal-icon-btn"
          onClick={goNext}
          disabled={disableNext}
          title="Mês seguinte"
        >
          <Icon name="chevron_right" />
        </button>
      </div>
      <div className="rec-cal-grid">
        {CAL_DOW.map((w) => (
          <span key={w} className="rec-cal-dow">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          // eslint-disable-next-line react/no-array-index-key
          if (!d) return <span key={`empty-${y}-${m}-${i}`} />;
          const isStart = fromD && t0(d) === t0(fromD);
          const isEnd = toD && t0(d) === t0(toD);
          const inRange = fromD && toD && d >= fromD && d <= toD;
          const sel = isStart || isEnd;
          const classes = ["rec-cal-day"];
          if (inRange && !isStart && !isEnd) classes.push("rec-cal-day-inr");
          if (inRange && isStart) classes.push("rec-cal-day-inr", "start");
          if (inRange && isEnd) classes.push("rec-cal-day-inr", "end");
          if (sel) classes.push("rec-cal-day-sel");
          return (
            <button
              type="button"
              key={d.toISOString()}
              className={classes.join(" ")}
              disabled={d > todayD}
              onClick={() => onPick(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

Calendar.propTypes = {
  from: PropTypes.string,
  to: PropTypes.string,
  onPick: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
  from: null,
  to: null,
};

export default function DateRangePopover({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(() => detectActivePreset(value.start, value.end));
  const [from, setFrom] = useState(value.start || "");
  const [to, setTo] = useState(value.end || "");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    setFrom(value.start || "");
    setTo(value.end || "");
    setPreset(detectActivePreset(value.start, value.end));
  }, [value.start, value.end]);

  const applyPreset = (p) => {
    const start = isoDate(shifted(p.days));
    const end = isoDate(today());
    setFrom(start);
    setTo(end);
    setPreset(p.key);
    onChange({ start, end });
    setOpen(false);
  };

  const enterCustom = () => {
    setPreset("custom");
  };

  const applyCustom = () => {
    if (!from || !to) return;
    onChange({ start: from, end: to });
    setOpen(false);
  };

  const onPick = (d) => {
    const ds = isoDate(d);
    if (!from || (from && to)) {
      setFrom(ds);
      setTo("");
    } else if (parseISO(ds) < parseISO(from)) {
      setFrom(ds);
    } else {
      setTo(ds);
    }
  };

  const triggerLabel = (() => {
    if (preset === "24h") return "Últimas 24h";
    const startD = parseISO(value.start);
    const endD = parseISO(value.end);
    if (!startD || !endD) return "Período";
    return `${fmtShortYear(startD)} — ${fmtShortYear(endD)}`;
  })();

  const rangeLabel = (() => {
    const startD = parseISO(from);
    const endD = parseISO(to);
    if (!startD && !endD) return "—";
    if (startD && !endD) return `${fmtShortYear(startD)} – …`;
    if (!startD && endD) return `… – ${fmtShortYear(endD)}`;
    return `${fmtShortYear(startD)} – ${fmtShortYear(endD)}`;
  })();

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className="rec-btn rec-btn-ghost"
        style={{ height: 36 }}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="calendar_month" />
        {triggerLabel}
        <Icon name="expand_more" />
      </button>
      {open && (
        <div className={`rec-pop${preset === "custom" ? " rec-pop-wide" : ""}`}>
          {PRESETS.map((p) => {
            const active = preset === p.key;
            return (
              <button
                type="button"
                key={p.key}
                className={`rec-pop-opt${active ? " on" : ""}`}
                onClick={() => applyPreset(p)}
              >
                <span>{p.label}</span>
                {active && <Icon name="check" className="ml-auto" style={{ fontSize: 16 }} />}
              </button>
            );
          })}
          <button
            type="button"
            className={`rec-pop-opt${preset === "custom" ? " on" : ""}`}
            onClick={enterCustom}
          >
            <span>Personalizado</span>
            {preset === "custom" && (
              <Icon name="check" className="ml-auto" style={{ fontSize: 16 }} />
            )}
          </button>
          {preset === "custom" && (
            <div className="rec-dr-custom">
              <Calendar from={from} to={to} onPick={onPick} />
              <div className="rec-dr-range-lbl">{rangeLabel}</div>
              <button
                type="button"
                className="rec-btn rec-btn-primary rec-btn-sm w-full"
                disabled={!from || !to}
                onClick={applyCustom}
              >
                <Icon name="check" />
                Aplicar período
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DateRangePopover.propTypes = {
  value: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
